import * as React from 'react';
import {FormLabel, FormControl, Select as SelectEl, MenuItem, FormHelperText, Box} from '@mui/material';
import { useMemo, memo } from 'react';

interface SelectProps {
    variant?: 'outlined' | 'filled' | 'standard';
    onChange: (event: any) => void;
    enums?: string[];
    label?: string;
    description?: string;
    required?: boolean;
    value: string;
    error?: string;
    enabled?: boolean;
    visible?: boolean;
    appValidated?: boolean;
    placeholder?: string;
}

const Select = memo((props: SelectProps) => {
    const {
        variant = 'outlined',
        onChange,
        enums,
        label,
        description,
        required = false,
        value = '',
        error,
        enabled = true,
        visible = true,
        appValidated = false,
        placeholder,
    } = props;

    const [appHasBeenValidated, setAppHasBeenValidated] = React.useState(false);

    React.useEffect(() => {
        if (appValidated) {
            setAppHasBeenValidated(true);
        }
    }, [appValidated]);

    const showRequiredError = useMemo(() =>
            required && appHasBeenValidated && !value,
        [required, appHasBeenValidated, value]
    );

    const showError = useMemo(() =>
            error !== undefined &&
            error !== '' &&
            error !== "must be equal to one of the allowed values",
        [error]
    );

    const hasError = showError || showRequiredError;
    let errorMessage = showRequiredError ? 'This field is required' : error;

    // Memoize computed values
    const showDescription = useMemo(() =>
            description !== undefined && description !== "",
        [description]
    );

    const menuItems = useMemo(() => [
        // Placeholder
        ...(placeholder ? [(
            <MenuItem key="placeholder" value="" disabled={required}>
                {placeholder}
            </MenuItem>
        )] : []),

        // Enum options excluding empty string and placeholder
        ...(enums?.filter(label => label !== "" && label !== placeholder)
            ?.map((label: string) => (
                <MenuItem key={label} value={label}>
                    {label}
                </MenuItem>
            )) ?? [])
    ], [enums, placeholder, required]);



    // Early return for hidden components
    if (!visible) {
        return null;
    }

    return (
        <Box sx={{ display: 'block' }}>
            <FormControl className="form-control" fullWidth={true}>
                <FormLabel required={required} className="form-separate-label">
                    {label}
                </FormLabel>
                <SelectEl
                    required={required}
                    error={hasError}
                    value={value}
                    onChange={onChange}
                    variant={variant}
                    disabled={!enabled}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                >
                    {menuItems}
                </SelectEl>
                {showDescription && (
                    <FormHelperText>{description}</FormHelperText>
                )}
                {hasError && (
                    <FormHelperText error={true}>
                        {errorMessage}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    );
});

Select.displayName = 'Select';

export default Select;
